<template>
  <div>
    <!-- New-mode judgment-selection step. Hoisted out of both variant components so that a judgment can be
         chosen without a variant being on screen: the variant it resolves to is not known until it is. The
         title is fixed and does not track the resolved variant, so the box never changes under the user. -->
    <naj-box
      v-if="showJudgementSelection"
      :title="local.caseRelatedRequest.cassationRequest.title"
    >
      <v-row>
        <v-col cols="6">
          <naj-input
            :type-id="formFieldType.selectbox"
            :value.sync="formModel.judgementCode"
            :label="local.caseRelatedRequest.cassationRequest.judgementCode"
            :items="judgments"
            item-value="judgementCode"
            item-text="judgementCode"
            validates="required"
          >
          </naj-input>
        </v-col>
      </v-row>
    </naj-box>

    <!-- Neither variant renders while the variant is unresolved. There is no fallback to Legacy and none to
         New, so no variant is ever shown and then replaced by the other. -->
    <component
      :is="variantComponentName"
      v-if="variantComponentName"
      :form-model="formModel"
      :required-data="requiredData"
      :root-form-model="rootFormModel"
      :view-mode="viewMode"
      :loaded-judgments="judgments"
    ></component>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import CassationRequest from './cassation-request.vue';
import CassationRequestLegacy from './cassation-request-legacy.vue';
import { CASSATION_VARIANT, getCassationVariantConfig } from './cassation-variant';

const { mapActions: mapCasesActions } = createNamespacedHelpers('CasesModule');

export default {
  name: 'CassationRequestSwitcher',
  components: {
    CassationRequest,
    CassationRequestLegacy,
  },
  inject: {
    $validator: '$validator',
    // provided by case-related-request/index.vue. Defaulted to null so the switcher stays renderable
    // wherever that provider is absent.
    registerCassationVariant: { default: null },
  },
  props: {
    formModel: {
      type: Object,
      default: () => ({}),
    },
    requiredData: {
      type: Object,
      default: () => ({}),
    },
    rootFormModel: {
      type: Object,
      default: () => ({}),
    },
    viewMode: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      judgments: [],
      hasLoadedJudgments: false,
      hasAttemptedLoad: false,
      loadingJudgments: false,
    };
  },
  computed: {
    isNewMode() {
      return this.$route.params.requestObjectKey === 'new';
    },
    selectedJudgement() {
      return (this.judgments || []).find(j => j.judgementCode == this.formModel.judgementCode);
    },
    /**
     * the single variant resolution for this feature — three states, one resolver.
     * `null` means unresolved: no variant component renders and no variant-specific parent behavior applies.
     */
    resolvedVariant() {
      // existing request (view / edit / reply): the variant it was created under is persisted on the request,
      // and that marker is authoritative on its own — it does not depend on the judgment list, so a failed or
      // empty list never leaves a submitted request unresolved.
      if (!this.isNewMode) {
        return this.formModel && this.formModel.isUpdatedCassationRequest
          ? CASSATION_VARIANT.new
          : CASSATION_VARIANT.legacy;
      }

      if (!this.hasLoadedJudgments) {
        return null;
      }

      // new request: resolve only from the selected judgment's rollout flag. `isUpdatedCassationRequest` is a
      // side effect of the New component having mounted, so it is never read here. There is no fallback:
      // until a valid judgment is selected the variant stays unresolved and neither component renders.
      const judgementCode = this.formModel && this.formModel.judgementCode;
      if (judgementCode === null || judgementCode === undefined || judgementCode === '') {
        return null;
      }

      const selected = this.selectedJudgement;
      if (!selected) {
        return null;
      }

      return selected.isCassationNewTemplate ? CASSATION_VARIANT.new : CASSATION_VARIANT.legacy;
    },
    variantComponentName() {
      // wait for the judgment load to settle before mounting a variant: both variant components fetch the
      // list themselves when `loaded-judgments` arrives empty, so mounting early would fire a second request.
      // settled includes failed and empty, so a submitted request still renders once the list has resolved.
      if (!this.judgmentsSettled) {
        return null;
      }

      if (this.resolvedVariant === CASSATION_VARIANT.new) {
        return 'CassationRequest';
      }

      if (this.resolvedVariant === CASSATION_VARIANT.legacy) {
        return 'CassationRequestLegacy';
      }

      return null;
    },
    /** the judgment load has finished, whether it succeeded, failed, or returned nothing. */
    judgmentsSettled() {
      return this.hasLoadedJudgments || this.judgmentsUnavailable;
    },
    /** no judgment can be offered — the list failed, came back empty, or holds nothing selectable. */
    judgmentsUnavailable() {
      return this.hasAttemptedLoad && !this.loadingJudgments && (this.judgments || []).length === 0;
    },
    showJudgementSelection() {
      return this.isNewMode && !this.judgmentsUnavailable;
    },
  },
  watch: {
    'formModel.judgementCode': {
      handler() {
        this.ensureJudgementsLoaded();
      },
      immediate: true,
    },
    'formModel.case.objectKey'() {
      this.hasLoadedJudgments = false;
      this.hasAttemptedLoad = false;
      this.judgments = [];
      this.ensureJudgementsLoaded();
    },
    resolvedVariant() {
      this.notifyVariant();
    },
  },
  mounted() {
    this.notifyVariant();
  },
  beforeDestroy() {
    this.notifyVariant(null);
  },
  methods: {
    ...mapCasesActions(['getCaseJudgments', 'getJudgementsByRequestObjectKey']),
    /**
     * hands the resolved variant, and the request-type configuration it implies, up to the shared parent.
     * Silently does nothing wherever the provider is absent.
     */
    notifyVariant(variant) {
      if (typeof this.registerCassationVariant !== 'function') {
        return;
      }

      const resolved = variant === undefined ? this.resolvedVariant : variant;

      this.registerCassationVariant({
        variant: resolved || null,
        requestTypeConfig: getCassationVariantConfig(resolved),
      });
    },
    ensureJudgementsLoaded() {
      if (!this.formModel || this.loadingJudgments) {
        return;
      }

      if (this.hasLoadedJudgments) {
        return;
      }

      this.loadJudgements();
    },
    loadJudgements() {
      if (!this.formModel || !this.formModel.case || !this.formModel.case.objectKey) {
        return;
      }

      this.loadingJudgments = true;
      this.hasAttemptedLoad = true;
      const params = {
        isPega: this.formModel.isPega,
        objectKey: this.formModel.case.objectKey,
      };

      const queryRequestObjectKey = this.$route.query.requestObjectKey;
      const isFromNonCaseParty = this.requiredData && this.requiredData.isFromNonCaseParty;
      const parentRequestObjectKey = this.requiredData && this.requiredData.parentRequestObjectKey;

      const sourcePromise = queryRequestObjectKey || isFromNonCaseParty
        ? this.getJudgementsByRequestObjectKey(queryRequestObjectKey || parentRequestObjectKey)
        : this.getCaseJudgments(params);

      sourcePromise
        .then((res) => {
          this.judgments = (res || {}).data || [];
          this.hasLoadedJudgments = true;
        })
        .catch(() => {
          this.judgments = [];
          this.hasLoadedJudgments = false;
        })
        .finally(() => {
          this.loadingJudgments = false;
        });
    },
  },
};
</script>
